import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getBrowserTimezone } from '../utils/dateUtils';

const TimezoneContext = createContext();

/**
 * Custom hook to access timezone context
 * @returns {Object} Timezone context value
 */
export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

/**
 * Build timezone list client-side using Intl API.
 * Returns array of { id, displayName, utcOffset, offsetHours }.
 */
function buildTimezoneList() {
  const now = new Date();
  const tzIds = Intl.supportedValuesOf('timeZone');

  const list = tzIds.map(id => {
    try {
      // Get UTC offset for this timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: id,
        timeZoneName: 'longOffset',
      });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(p => p.type === 'timeZoneName');
      const offsetStr = offsetPart?.value || 'UTC';

      // Parse offset hours from "GMT+10:00" or "GMT-05:00" or "GMT"
      let offsetHours = 0;
      const match = offsetStr.match(/GMT([+-])(\d{1,2}):?(\d{2})?/);
      if (match) {
        const sign = match[1] === '+' ? 1 : -1;
        offsetHours = sign * (parseInt(match[2], 10) + (parseInt(match[3] || '0', 10) / 60));
      }

      // Format as UTC+HH:MM
      const absH = Math.floor(Math.abs(offsetHours));
      const absM = Math.round((Math.abs(offsetHours) - absH) * 60);
      const utcOffset = `UTC${offsetHours >= 0 ? '+' : '-'}${String(absH).padStart(2, '0')}:${String(absM).padStart(2, '0')}`;

      // Display name: readable timezone ID
      const displayName = id.replace(/_/g, ' ');

      return { id, displayName, utcOffset, offsetHours };
    } catch {
      return null;
    }
  }).filter(Boolean);

  // Sort by offset, then by display name
  list.sort((a, b) => a.offsetHours - b.offsetHours || a.displayName.localeCompare(b.displayName));

  return list;
}

/**
 * Timezone Provider Component
 * Manages user's timezone preference and provides formatting utilities
 */
export const TimezoneProvider = ({ children }) => {
  const { user } = useAuth();

  // Generate timezone list client-side (no API dependency)
  const timezones = useMemo(() => buildTimezoneList(), []);

  // Get the effective timezone (user preference or browser default)
  const effectiveTimezone = user?.timezoneId || getBrowserTimezone();

  /**
   * Get timezone display name for a given timezone ID
   * @param {string|null} timezoneId - IANA timezone identifier
   * @returns {string} Display name
   */
  const getTimezoneDisplayName = useCallback((timezoneId) => {
    if (!timezoneId) return 'Browser Default';

    const timezone = timezones.find(tz =>
      tz.id.toLowerCase() === timezoneId.toLowerCase()
    );

    return timezone ? timezone.displayName : timezoneId;
  }, [timezones]);

  /**
   * Check if a timezone ID is valid
   * @param {string} timezoneId - IANA timezone identifier
   * @returns {boolean} True if valid
   */
  const isValidTimezone = useCallback((timezoneId) => {
    if (!timezoneId) return true; // null is valid (browser default)
    return timezones.some(tz =>
      tz.id.toLowerCase() === timezoneId.toLowerCase()
    );
  }, [timezones]);

  /**
   * Get timezones grouped by UTC offset for easier selection
   * @returns {Object} Timezones grouped by offset
   */
  const getGroupedTimezones = useCallback(() => {
    const grouped = {};

    timezones.forEach(tz => {
      const offset = tz.utcOffset || 'UTC+00:00';
      if (!grouped[offset]) {
        grouped[offset] = [];
      }
      grouped[offset].push(tz);
    });

    return grouped;
  }, [timezones]);

  const value = {
    // State
    timezones,
    loadingTimezones: false,
    timezoneError: null,
    effectiveTimezone,

    // Methods
    fetchTimezones: () => {}, // no-op, timezones are generated client-side
    getTimezoneDisplayName,
    isValidTimezone,
    getGroupedTimezones,

    // Convenience
    browserTimezone: getBrowserTimezone(),
    userTimezone: user?.timezoneId || null,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

export default TimezoneContext;
